<template>
    <div class="builder-modal">
        <DsModal
            class="sms-broadcast-builder-modal"
            :is-open="isOpen"
            show-close
            size="fullscreen"
            :title="modalTitle"
            @close="close"
        >
            <template #header>
                <header class="stage-indicator-container">
                    <StageIndicator
                        :active-stage="activeStage"
                        :stages="stages"
                        @go-to-stage="goToStage"
                    />
                </header>

                <div class="header-actions">
                    <DsIconButton
                        name="settings"
                        class="sms-broadcast-settings-button"
                        @click="handleOpenSettings"
                    />
                    <DsTextButton
                        class="send-test-button"
                        :disabled="isSmsSendingBlockedByVerificationStatus"
                        @click="handleSendTest"
                    >
                        {{ $options.MESSAGE_CONSTANTS.sendTest }}
                    </DsTextButton>
                </div>
            </template>

            <template #default>
                <SmsBroadcastWriteStage
                    v-if="showWriteStage"
                    :broadcast="broadcast"
                    :contact-count="contactCount"
                    :opt-statuses="optStatuses"
                    :recipients="recipients"
                    :show-error="showSendTestError"
                    :status="writeStageStatus"
                    :from="fromNumber"
                    :contacts-with-multiple-phones="contactsWithMultiplePhones"
                    :sms-broadcast-enabled="smsBroadcastEnabled"
                    :app-id="appId"
                    :user-id="userId"
                    :sms-account="smsAccount"
                    :sms-account-verification="smsAccountVerification"
                    @update="handleBroadcastUpdate"
                    @next="action"
                    @show-settings="isSettingsModalOpen = true"
                />

                <SmsBroadcastSendStage
                    v-if="showSendStage"
                    :broadcast="broadcast"
                    :contact-count="contactCount"
                    :opt-statuses="optStatuses"
                    :recipients="recipients"
                    :sending="sending"
                    :app-id="appId"
                    :user-id="userId"
                    :sms-account="smsAccount"
                    :sms-account-verification="smsAccountVerification"
                    @input="updateSchedule"
                    @send="action"
                    @back="back"
                    @update="handleAccountUpdate"
                    @settings="handleOpenSettings"
                />
            </template>
        </DsModal>

        <portal to="root">
            <SmsSendTestModal
                :from="fromNumber"
                :is-open="isSendTestModalOpen"
                :message="broadcast.messageContent"
                @close="isSendTestModalOpen = false"
            />
        </portal>

        <portal to="root">
            <TextMarketingSettingsModal
                v-if="isSettingsModalOpen"
                :is-open="isSettingsModalOpen"
                :is-billing-changes-enabled="isBillingChangesEnabled"
                :account="smsAccount"
                @close="isSettingsModalOpen = false"
                @update="handleAccountUpdate"
            />

            <TextConsentConfirmationModal
                v-if="showConsentModal"
                :is-open="showConsentModal"
                :numbers-to-be-single-opted-in="numbersToBeSingleOptedIn"
                :user-id="userId"
                @close="handleConsentModalClose"
                @continue="handleAcceptCompliance"
            />
        </portal>

        <portal-target name="kmnTemplateManager" />
    </div>
</template>

<script>
import debounce from 'lodash.debounce';

import SmsBroadcastWriteStage from '@/components/broadcast/sms/SmsBroadcastWriteStage';
import SmsBroadcastSendStage from '@/components/broadcast/sms/SmsBroadcastSendStage';
import StageIndicator from '@/components/broadcast/StageIndicator';
import SmsSendTestModal from '@/components/automatedSms/SmsSendTestModal';
import TextConsentConfirmationModal from '@/components/automatedSms/TextConsentConfirmationModal';
import TextMarketingSettingsModal from '@/components/automatedSms/TextMarketingSettingsModal';
import {
    PHONE_FIELDS,
    SUPPORTED_PHONE_TYPES,
} from '@/components/automatedSms/sms.constants';
import { containsSupportedCharacters } from '@/components/automatedSms/automatedSms.util';
import {
    trackSmsBroadcastSent,
    trackKmnBroadcastLimitExceeded,
} from '@/components/automatedSms/smsAnalytics';

import {
    KMN_VERIFICATION_STATUS_TYPE,
    MAX_RECIPIENTS,
    SMS_MESSAGE_LENGTH,
    SMS_OPT_STATUSES,
    SMS_WIZARD_STAGE,
    STAGE_STATUS,
} from '@/components/broadcast/constants/smsBroadcast.constants';

import { INPUT_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';
import {
    createSmsBroadcast,
    updateSmsBroadcast,
    scheduleSmsBroadcast,
    getRecipientList,
    getRecipientPhoneStatus,
    getSmsBroadcast,
} from '@/api/broadcasts/smsBroadcasts';

import {
    getMessageLength,
    getLongestMergeableFieldLength,
    getValidRecipientCount,
    recipientHasValidNumber,
    getSortedValidRecipients,
    getScheduleDate,
} from '@/components/broadcast/utils/smsBroadcast.util';

import {
    isValidNorthAmericaPhoneNumber,
    normalizeNorthAmericaPhoneNumber,
} from '@/components/broadcast/utils/sms-utils';


export const MESSAGE_CONSTANTS = {
    newSmsTitle: 'New text message broadcast',
    editSmsTitle: 'Edit text message broadcast',
    sendTest: 'Send test',
    successMessage: 'Your text broadcast is being sent.',
    sendingError: 'There was an error sending your broadcast. Please try again.',
};

export default {
    MESSAGE_CONSTANTS,

    components: {
        SmsBroadcastWriteStage,
        SmsBroadcastSendStage,
        TextConsentConfirmationModal,
        TextMarketingSettingsModal,
        StageIndicator,
        SmsSendTestModal,
    },

    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        inputDelay: {
            type: Number,
            default: INPUT_DEBOUNCE_DELAY,
        },
        fromNumber: {
            type: String,
            required: true,
        },
        userId: {
            type: Number,
            required: true,
        },
        appId: {
            type: String,
            required: true,
        },
        broadcastId: {
            type: Number,
            default: 0,
        },
        copiedBroadcastId: {
            type: Number,
            default: 0,
        },
        title: {
            type: String,
            default: '',
        },
        messageContent: {
            type: String,
            default: '',
        },
        smsAccount: {
            type: Object,
            default: () => ({}),
        },
        smsAccountVerification: {
            type: Object,
            default: () => ({}),
        },
        smsBroadcastEnabled: Boolean,
        isBillingChangesEnabled: Boolean,
    },

    data() {
        return {
            isSendTestModalOpen: false,
            isSettingsModalOpen: false,
            showConsentModal: false,
            showError: false,
            contacts: [],
            broadcast: {
                id: 0,
                messageContent: '',
                recipientCriteria: [],
                schedule: {
                    timing: 'sendNow',
                    timingDetails: {},
                },
                title: '',
                phoneTypes: [],
                phoneFields: [],
            },
            optStatuses: [],
            showSendTestError: false,
            stages: [
                {
                    disabled: false,
                    errorTooltip: 'writeError',
                    label: 'Write',
                    showError: false,
                    status: STAGE_STATUS.IN_PROGRESS,
                },
                {
                    disabled: false,
                    errorTooltip: 'sendError',
                    label: 'Review and send',
                    showError: false,
                    status: STAGE_STATUS.IN_PROGRESS,
                },
            ],
            sending: false,
            stageId: 0,
            creatingBroadcast: false,
        };
    },

    async mounted() {
        if (this.inputDelay > 0) {
            this.handleBroadcastUpdate = debounce(this.handleBroadcastUpdate, this.inputDelay);
        }

        if (this.broadcastId > 0) {
            // edit existing SMS message
            this.broadcast = await getSmsBroadcast(this.broadcastId);
            await this.loadRecipientsAndOptStatuses();
        } else if (this.copiedBroadcastId > 0) {
            const broadcastToCopy = await getSmsBroadcast(this.copiedBroadcastId);

            this.broadcast.title = broadcastToCopy.title;
            this.broadcast.messageContent = broadcastToCopy.messageContent;
            this.broadcast.recipientCriteria = broadcastToCopy.recipientCriteria;
            this.broadcast.phoneTypes = broadcastToCopy.phoneTypes;
            this.broadcast.phoneFields = broadcastToCopy.phoneFields;
            await this.loadRecipientsAndOptStatuses();
        } else if (this.title !== '' || this.messageContent !== '') {
            this.broadcast.title = this.title;
            this.broadcast.messageContent = this.messageContent;
        }

        const {
            messageContent,
            recipientCriteria,
            title,
        } = this.broadcast;

        this.broadcast.title = title || '';
        this.broadcast.messageContent = messageContent || '';
        this.broadcast.recipientCriteria = Array.isArray(recipientCriteria) ? recipientCriteria : [];
        this.broadcast.schedule = {
            timing: 'sendNow',
            timingDetails: {},
        };

        this.setStagesEligibilty();
    },

    computed: {
        isMessageTooLong() {
            return this.broadcast.messageContent?.length > this.maxCharacterCount;
        },

        isMessageValid() {
            return this.broadcast.messageContent?.length > 0 && !this.isMessageTooLong;
        },

        showWriteStage() {
            return this.stageId === SMS_WIZARD_STAGE.WRITE;
        },

        contactCount() {
            return this.contacts?.length || 0;
        },

        recipients() {
            const upperPhoneTypes = this.broadcast.phoneTypes.map((phoneType) => phoneType.toUpperCase());
            const upperPhoneFields = this.broadcast.phoneFields.map((phoneField) => phoneField.toUpperCase());

            const recipients = this.contacts.reduce((numbers, contact) => {
                const phoneNumbers = this.getContactPhoneNumbers(contact);

                const filteredPhoneNumbers = phoneNumbers
                    .filter(({ type, field }) => {
                        let phoneType = type;

                        const supportedPhoneTypesUpper = SUPPORTED_PHONE_TYPES.map((supportedPhoneType) => supportedPhoneType.toUpperCase());

                        if (!type || !supportedPhoneTypesUpper.includes((type).toUpperCase())) {
                            phoneType = 'Other';
                        }

                        return upperPhoneTypes.includes((phoneType).toUpperCase()) || upperPhoneFields.includes((field).toUpperCase());
                    })
                    .map(({ number }) => ({ phoneNumber: number, mergeableField: contact.firstName, contactId: contact.id }));

                return numbers.concat(filteredPhoneNumbers);
            }, []);

            return recipients;
        },

        shouldShowCompliance() {
            return this.numbersToBeSingleOptedIn.length > 0;
        },

        numbersToBeSingleOptedIn() {
            const { optStatuses, recipients } = this;
            const { UNCONFIRMED, EXPIRED } = SMS_OPT_STATUSES;

            const numbersWithStatusToBeOmitted = optStatuses
                .filter(({ statusKind }) => ![UNCONFIRMED, EXPIRED].includes(statusKind))
                .map(({ phoneNumber }) => normalizeNorthAmericaPhoneNumber(phoneNumber));

            return recipients
                .filter(({ phoneNumber }) => !numbersWithStatusToBeOmitted.includes(normalizeNorthAmericaPhoneNumber(phoneNumber)))
                .filter(({ phoneNumber }) => isValidNorthAmericaPhoneNumber(phoneNumber))
                .map(({ phoneNumber }) => normalizeNorthAmericaPhoneNumber(phoneNumber));
        },

        contactsWithMultiplePhones() {
            const upperPhoneTypes = this.broadcast.phoneTypes.map((phoneType) => phoneType.toUpperCase());
            const upperPhoneFields = this.broadcast.phoneFields.map((phoneField) => phoneField.toUpperCase());

            return this.contacts.filter((contact) => {
                const phoneNumbers = this.getContactPhoneNumbers(contact);

                const filteredPhoneNumbers = phoneNumbers?.filter(({ type, field }) => {
                    let phoneType = type;

                    const supportedPhoneTypesUpper = SUPPORTED_PHONE_TYPES.map((supportedPhoneType) => supportedPhoneType.toUpperCase());

                    if (!type || !supportedPhoneTypesUpper.includes((type).toUpperCase())) {
                        phoneType = 'Other';
                    }

                    return upperPhoneTypes.includes((phoneType).toUpperCase()) || upperPhoneFields.includes((field).toUpperCase());
                }) || [];

                return filteredPhoneNumbers.length > 1;
            });
        },
        writeStageStatus() {
            return this.stages[SMS_WIZARD_STAGE.WRITE].status;
        },
        sendStageStatus() {
            return this.stages[SMS_WIZARD_STAGE.SEND].status;
        },
        showSendStage() {
            return this.stageId === SMS_WIZARD_STAGE.SEND;
        },
        activeStage() {
            return this.stageId;
        },
        isBroadcastValid() {
            const {
                messageContent,
                recipientCriteria,
                title,
            } = this.broadcast;
            const {
                optStatuses,
                recipients,
            } = this;
            const totalValidContactCount = getValidRecipientCount({ optStatuses, recipients });
            const longestMergeableFieldLength = getLongestMergeableFieldLength(recipients);
            const { length: messageLength } = getMessageLength(messageContent, longestMergeableFieldLength);


            return Boolean(this.fromNumber)
                    && Boolean(title)
                    && recipientCriteria.length > 0
                    && totalValidContactCount > 0
                    && Boolean(messageContent)
                    && messageLength <= this.maxCharacterCount
                    && this.messageContainsValidCharacters;
        },
        modalTitle() {
            if (this.broadcastId > 0) {
                return this.$options.MESSAGE_CONSTANTS.editSmsTitle;
            }

            return this.$options.MESSAGE_CONSTANTS.newSmsTitle;
        },

        messageContainsValidCharacters() {
            return containsSupportedCharacters(this.broadcast.messageContent);
        },

        maxCharacterCount() {
            return SMS_MESSAGE_LENGTH.MAXIMUM;
        },

        isSmsSendingBlockedByVerificationStatus() {
            return this.smsAccountVerification.status
                && this.smsAccountVerification.status !== KMN_VERIFICATION_STATUS_TYPE.APPROVED;
        },
    },

    methods: {
        getContactPhoneNumbers(contact) {
            const phoneNumbers = [];

            if (contact.phoneNumber) {
                phoneNumbers.push({
                    number: contact.phoneNumber,
                    type: contact.phoneType,
                    field: PHONE_FIELDS.PHONE1,
                });
            }

            if (contact.phone2) {
                phoneNumbers.push({
                    number: contact.phone2,
                    type: contact.phone2Type,
                    field: PHONE_FIELDS.PHONE2,
                });
            }

            if (contact.phone3) {
                phoneNumbers.push({
                    number: contact.phone3,
                    type: contact.phone3Type,
                    field: PHONE_FIELDS.PHONE3,
                });
            }

            if (contact.phone4) {
                phoneNumbers.push({
                    number: contact.phone4,
                    type: contact.phone4Type,
                    field: PHONE_FIELDS.PHONE4,
                });
            }

            if (contact.phone5) {
                phoneNumbers.push({
                    number: contact.phone5,
                    type: contact.phone5Type,
                    field: PHONE_FIELDS.PHONE5,
                });
            }

            return phoneNumbers;
        },

        handleSendTest() {
            if (this.isMessageValid) {
                this.isSendTestModalOpen = true;
            } else {
                this.showSendTestError = true;
            }
        },

        handleOpenSettings() {
            this.isSettingsModalOpen = true;
        },

        handleAccountUpdate(account) {
            this.$emit('update', account);
        },

        async handleAcceptCompliance() {
            this.showConsentModal = false;
            await this.loadRecipientsAndOptStatuses();
            await this.goToStage(this.stageId + 1);
        },

        handleConsentModalClose() {
            this.showConsentModal = false;
        },

        action() {
            if (this.showSendStage) {
                if (this.isBroadcastValid) {
                    this.sendBroadcast();
                } else {
                    this.goToStage(SMS_WIZARD_STAGE.WRITE);
                }
            } else {
                this.next();
            }
        },

        async next() {
            this.checkValidity();

            if (this.isBroadcastValid && this.shouldShowCompliance) {
                this.showConsentModal = true;
            } else if (this.isBroadcastValid && !this.shouldShowCompliance) {
                await this.goToStage(this.stageId + 1);
            }
        },

        async handleBroadcastUpdate({ broadcast }) {
            this.broadcast = {
                ...this.broadcast,
                ...broadcast,
            };

            await this.loadRecipientsAndOptStatuses();

            await this.createOrUpdateBroadcast();

            if (this.broadcast.messageContent) {
                this.showSendTestError = false;
            }

            if (this.writeStageStatus === STAGE_STATUS.INVALID) {
                this.checkValidity();
            }

            if (this.recipients.length > MAX_RECIPIENTS) {
                this.logBroadcastLimitAmplitudeEvent();
            }
        },

        logBroadcastLimitAmplitudeEvent() {
            const { appId, recipients } = this;
            const { recipientCriteria } = this.broadcast;

            const recipientsAdded = recipientCriteria.map((recipient) => {
                const currentRecipient = { type: recipient.type };

                if (recipient.type !== 'SINGLECONTACTTAG') {
                    currentRecipient.name = recipient.name;
                }

                return currentRecipient;
            });

            trackKmnBroadcastLimitExceeded({
                appId,
                recipientCount: recipients.length,
                recipientsAdded,
            });
        },

        async loadRecipientsAndOptStatuses() {
            const { recipientCriteria } = this.broadcast;

            if (Array.isArray(recipientCriteria) && recipientCriteria.length > 0) {
                this.contacts = await this.getRecipientPhoneNumbers(recipientCriteria);

                this.optStatuses = await this.getOptStatuses(this.recipients);
            } else {
                this.contacts = [];

                this.optStatuses = [];
            }
        },

        async getRecipientPhoneNumbers(recipientCriteria) {
            const tagIds = [];
            const savedSearchIds = [];
            const singleContactTagIds = [];

            recipientCriteria.forEach((criteria) => {
                switch (criteria.type) {
                case 'TAG':
                    tagIds.push(criteria.id);
                    break;
                case 'SAVEDSEARCH':
                    savedSearchIds.push(criteria.id);
                    break;
                case 'SINGLECONTACTTAG':
                    singleContactTagIds.push(criteria.id);
                    break;
                default: break;
                }
            });

            if (tagIds.length > 0 || savedSearchIds.length > 0 || singleContactTagIds.length > 0) {
                try {
                    const { autocompleteContactList: contacts } = await getRecipientList({
                        tag: tagIds.toString(),
                        savedSearch: savedSearchIds.toString(),
                        singleContactTag: singleContactTagIds.toString(),
                    });

                    let mergedContacts = [];

                    contacts.forEach((contactList) => {
                        if (contactList.contacts.length > 0) {
                            mergedContacts = [...mergedContacts, ...contactList.contacts];
                        }
                    });

                    return mergedContacts;
                } catch (error) {
                    return [];
                }
            }

            return [];
        },

        async getOptStatuses(recipients) {
            const validNumbers = recipients
                .filter(recipientHasValidNumber)
                .map(({ phoneNumber }) => phoneNumber);

            const params = {
                fromNumber: this.fromNumber,
                phoneNumbers: validNumbers.join(),
            };

            const { optInStatuses } = await getRecipientPhoneStatus(params);

            return optInStatuses || [];
        },

        async createOrUpdateBroadcast() {
            if (!this.broadcast.id) {
                if (!this.creatingBroadcast) {
                    this.creatingBroadcast = true;

                    const { id } = await this.createBroadcast();

                    this.creatingBroadcast = false;

                    this.broadcast.id = id;
                }
            } else {
                this.updateBroadcast();
            }
        },

        createBroadcast() {
            this.broadcast.createdBy = this.userId;

            return createSmsBroadcast(this.broadcast);
        },

        updateBroadcast() {
            this.broadcast.updatedBy = this.userId;

            return updateSmsBroadcast(this.broadcast);
        },

        checkValidity() {
            if (this.isBroadcastValid) {
                this.stages[SMS_WIZARD_STAGE.WRITE].status = STAGE_STATUS.COMPLETE;
            } else {
                this.stages[SMS_WIZARD_STAGE.WRITE].status = STAGE_STATUS.INVALID;
            }
        },

        updateSchedule({
            timing, date, time, timeZone,
        }) {
            this.broadcast = {
                ...this.broadcast,
                schedule: {
                    timing,
                    timingDetails: timing === 'sendNow' ? {} : {
                        date,
                        time,
                        timeZone,
                    },
                },
            };
        },

        back() {
            this.goToStage(this.stageId - 1);
        },

        setStagesEligibilty() {
            if (this.isSmsSendingBlockedByVerificationStatus) {
                for (let i = 1; i < this.stages.length; i++) {
                    this.stages[i].disabled = true;
                }
            }
        },

        async goToStage(stageIndex) {
            if (this.stages[stageIndex].disabled) {
                return;
            }

            if (stageIndex !== this.stageId) {
                this.checkValidity();

                if (this.shouldShowCompliance) {
                    this.showConsentModal = true;

                    return;
                }

                if (this.showWriteStage && !this.isBroadcastValid) {
                    return;
                }

                if (this.isBroadcastValid && !this.broadcast.id) {
                    const { id } = await this.createBroadcast();

                    this.broadcast.id = id;

                    if (this.showWriteStage) {
                        this.stageId = 1;

                        return;
                    }
                }

                this.stageId = stageIndex;
            }
        },

        async sendBroadcast() {
            this.sending = true;

            const { optStatuses, recipients } = this;

            const validRecipients = getSortedValidRecipients({ optStatuses, recipients });

            const trimmedRecipients = [...validRecipients].slice(0, MAX_RECIPIENTS);
            const {
                id, schedule, messageContent, phoneTypes, phoneFields,
            } = this.broadcast;
            const scheduleDate = getScheduleDate(schedule);

            try {
                this.broadcast = await scheduleSmsBroadcast({
                    id,
                    recipients: trimmedRecipients,
                    scheduleDate,
                    updatedBy: this.userId,
                });
                this.$toast({ message: this.$options.MESSAGE_CONSTANTS.successMessage });

                trackSmsBroadcastSent({
                    recipientCount: recipients.length,
                    messageLength: messageContent.length,
                    phoneTypes,
                    phoneFields,
                    sendType: schedule.timing,
                });

                this.close();
            } catch (error) {
                this.$toast({ message: this.$options.MESSAGE_CONSTANTS.sendingError });
            }
        },

        close() {
            this.$emit('close', 'sms');
        },
    },
};
</script>

<style lang="scss" scoped>
.sms-broadcast-builder-modal {
    --modal-padding: 0;
    background-color: blue;
    overflow: hidden;

    div.modal-body {
        padding: 0;
    }
}

.header-actions {
    display: flex;
    gap: $spacing-100;
    align-items: center;
    padding: 0 $spacing-250;
}

.send-test-button {
    font-size: 0.875rem;
    font-weight: 500;
}

.stage-indicator-container {
    position: fixed;
    top: 0;
    left: 0;
    height: 1px;
    z-index: 10;
    width: calc(100% - 3rem);
    margin-bottom: $spacing-200;
    text-align: center;
}

</style>

<style lang="scss">
    div.sms-broadcast-builder-modal {
        div.modal-header{
            padding: $spacing-100;
        }

        div.modal-body {
            padding: 0;
        }
    }
</style>
